import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { memo } from 'react';
import { BsFillGearFill } from 'react-icons/bs';
import { LuCircleOff, LuColumns3, LuGrid3X3, LuHourglass, LuMaximize2 } from 'react-icons/lu';

import { getApiErrorMessage } from '~/api/client';
import { hardnessDomainByBlastQueryOptions, optimiserByBlastQueryOptions } from '~api/queries';
import type { Blast, HardnessDomainResponse, PatternDesignOptimisation } from '~api/services';
import { Skeleton } from '~components';
import { Button } from '~components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '~components/ui/card';
import { Polygon, SvgCanvas } from '~libs';
import { type OptimisationsJobState, useBlastOptimisationJob } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type BlastItemProps = {
  /** Blast metadata to display in the item */
  blast: Blast;
  /** Optional additional class name for styling */
  className?: string;
};

type SummaryItem = {
  /** Icon component to visually represent the optimisation parameter */
  icon: React.ComponentType<{ className?: string; 'aria-label'?: string }>;
  /** Label to describe the optimisation parameter (e.g., "m burden", "mm diameter") */
  label: string;
  /** Accessible label for the icon, describing the optimisation parameter for screen readers */
  ariaLabel: string;
  /** Function to extract the value to display from the optimisation data */
  getValue: (opt: PatternDesignOptimisation) => string | number;
};

type BlastItemOptimisationSummaryProps = {
  /** The best optimisation scenario for the blast, if available */
  optimisation?: PatternDesignOptimisation;
  /** The current state of the optimisations job, used to determine what message to display if no optimisation is available */
  optimisationsJobState: OptimisationsJobState;
};

type BlastItemActionsProps = {
  /** The name of the blast, used for linking to optimisations and blast design context pages */
  blastName: string;
  /** Whether optimisations are available for the blast, used to determine which call-to-action to show */
  hasOptimisations: boolean;
  /** Whether the optimisations data is currently loading, used to show a loading state for the optimisations action */
  isLoadingOptimisations: boolean;
  /** Whether the blast design context data is currently loading, used to show a loading state for the blast design context action */
  isLoadingBlastDesignContext: boolean;
  /** The current state of the optimisations job, used to determine whether the "Build optimisations" button should be disabled or show a loading state */
  optimisationsJobState: OptimisationsJobState;
  /** Callback function to trigger the build of optimisations when the "Build optimisations" button is clicked */
  onBuildOptimisations: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const SUMMARY_ITEMS: SummaryItem[] = [
  { icon: LuMaximize2, label: 'm burden', ariaLabel: 'Burden', getValue: (opt) => opt.burden },
  { icon: LuColumns3, label: 'm spacing', ariaLabel: 'Spacing', getValue: (opt) => opt.spacing },
  {
    icon: LuCircleOff,
    label: 'mm diameter',
    ariaLabel: 'Diameter',
    getValue: (opt) => (opt.diameter * 1000).toFixed(0),
  },
  { icon: LuGrid3X3, label: 'holes', ariaLabel: 'Holes', getValue: (opt) => opt.holes },
];

const DEFAULT_CANNOT_OPTIMISE_MESSAGE = 'This blast cannot be optimised.';

const NO_OPTIMISATIONS_MESSAGE = 'No optimisation scenarios available.';

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/**
 * Component to display the status message for blast optimisations based on the current optimisations job state.
 * It handles various states such as triggering, running, errors, and reasons for non-optimisation,
 * providing clear feedback to the user about the optimisations status.
 */
const OptimisationStatusMessage = ({ optimisationsJobState }: { optimisationsJobState: OptimisationsJobState }) => {
  switch (optimisationsJobState.state) {
    case 'trigger-error':
      return (
        <>Oops! Something went wrong: {getApiErrorMessage(optimisationsJobState.error)}. Please try again later.</>
      );
    case 'cannot-optimise':
      return optimisationsJobState.reasons.length > 0
        ? `This blast cannot be optimised because it has ${optimisationsJobState.reasons.join(', ')}`
        : DEFAULT_CANNOT_OPTIMISE_MESSAGE;
    case 'failed':
      return optimisationsJobState.reasons.length > 0
        ? `Optimisations failed! ${optimisationsJobState.reasons.join(', ')}`
        : 'Optimisations failed for an unknown reason.';
    case 'running':
      return (
        <span className="flex animate-pulse items-start justify-center gap-1 text-info">
          Optimisations are running... Waiting for the results to be available.
        </span>
      );
    case 'triggering':
      return (
        <span className="flex animate-pulse items-center justify-center gap-1 text-info">
          Starting optimisations build... Please wait.
        </span>
      );
    case 'idle':
      return <>{NO_OPTIMISATIONS_MESSAGE}</>;
  }
};

/**
 * Component to display a summary of the best optimisation scenario for a blast,
 * including key parameters such as burden, spacing, diameter, and number of holes.
 * If no optimisation is available, it shows an appropriate message, including reasons if the blast cannot be optimised.
 */
const BlastItemOptimisationSummary = ({ optimisation, optimisationsJobState }: BlastItemOptimisationSummaryProps) => {
  if (optimisation) {
    return (
      <div className="blast-item--optimisation-summary">
        <h4 className="mb-2 font-bold text-muted-foreground text-xs uppercase">Best Optimisation</h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-foreground text-sm">
          {SUMMARY_ITEMS.map(({ icon: Icon, label, ariaLabel, getValue }) => (
            <div key={label} className="flex items-center gap-1.5">
              <Icon className="size-3" aria-label={ariaLabel} />
              <span className="leading-normal">
                {getValue(optimisation)} {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="blast-item--optimisation-summary">
      <h4 className="mb-2 font-bold text-muted-foreground text-xs uppercase">Best Optimisation</h4>
      <div className="flex text-pretty px-4 py-1 text-destructive text-sm">
        <OptimisationStatusMessage optimisationsJobState={optimisationsJobState} />
      </div>
    </div>
  );
};
BlastItemOptimisationSummary.displayName = 'BlastItemOptimisationSummary';

/**
 * Component to display a summary of the blast design context information for a blast,
 * showing the name and source of the block model used for blast design context generation.
 * If no blast design context data is available, it shows an appropriate message.
 */
const BlastItemDesignContextSummary = ({ blastDesignContext }: { blastDesignContext?: HardnessDomainResponse }) => {
  const { block_model } = blastDesignContext || {};

  return (
    <div className="blast-item--design-context-summary">
      <h4 className="mb-2 font-bold text-muted-foreground text-xs uppercase">Blast Design Context</h4>
      {!block_model ? (
        <div className="rounded-md px-4 py-1">
          <p className="text-pretty text-destructive text-sm">Drill domains have not yet been generated</p>
        </div>
      ) : (
        <div className="truncate text-foreground text-xs" title={`${block_model.name} (${block_model.source})`}>
          {block_model.name} ({block_model.source})
        </div>
      )}
    </div>
  );
};
BlastItemDesignContextSummary.displayName = 'BlastItemDesignContextSummary';

/**
 * Component to display the call-to-action button for building or viewing optimisations for a blast.
 * - If optimisations are available, it shows a "View optimisations" button linking to the optimisations page.
 * - If no optimisations are available but the job is in progress, it shows a disabled "Building optimisations" button with a loading icon.
 * - If no optimisations are available and the job is not in progress, it shows an enabled "Build optimisations" button that triggers the build when clicked.
 */
const BuildOptimisationsButton = ({
  blastName,
  hasOptimisations,
  isBusy,
  isDisabled,
  onBuild,
}: {
  blastName: string;
  hasOptimisations: boolean;
  isBusy: boolean;
  isDisabled: boolean;
  onBuild: () => void;
}) => {
  if (hasOptimisations) {
    return (
      <Button className="w-full" disabled={isDisabled} asChild>
        <Link params={{ blastName }} to="/blasts/$blastName/optimisations">
          View optimisations
        </Link>
      </Button>
    );
  }

  const Icon = isBusy ? LuHourglass : BsFillGearFill;

  return (
    <Button className="w-full" disabled={isDisabled} onClick={onBuild}>
      <Icon className="flex" />
      {isBusy ? 'Building optimisations' : 'Build optimisations'}
    </Button>
  );
};

/**
 * Component to display the call-to-action buttons for a blast item,
 * including options to view/build optimisations and view blast design context.
 */
const BlastItemActions = ({
  blastName,
  hasOptimisations,
  isLoadingOptimisations,
  isLoadingBlastDesignContext,
  optimisationsJobState,
  onBuildOptimisations,
}: BlastItemActionsProps) => {
  const isBusy = optimisationsJobState.state === 'running' || optimisationsJobState.state === 'triggering';
  const isDisabled = optimisationsJobState.state === 'cannot-optimise' || isBusy;

  return (
    <div className="space-y-2">
      {isLoadingOptimisations ? (
        <Skeleton className="h-11 w-full" />
      ) : (
        <BuildOptimisationsButton
          blastName={blastName}
          hasOptimisations={hasOptimisations}
          isBusy={isBusy}
          isDisabled={isDisabled}
          onBuild={onBuildOptimisations}
        />
      )}
      {isLoadingBlastDesignContext ? (
        <Skeleton className="h-11 w-full" />
      ) : (
        <Button className="w-full" variant="outline" asChild>
          <Link params={{ blastName }} to="/blasts/$blastName/design_context">
            Blast Design Context
          </Link>
        </Button>
      )}
    </div>
  );
};
BlastItemActions.displayName = 'BlastItemActions';

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * The BlastItem component represents a single blast in the blast list,
 * displaying key information, summary of the best optimisation scenario (if available).
 * It also provides a call to action to
 *  - View optimisations or Build optimisations (if possible)
 *  - View blast design context
 *
 * Note: The component is memoized to prevent unnecessary re-renders when the blast data does not change.
 *
 * @example
 * ```tsx
 * <BlastItem blast={blastData} />
 * ```
 */
const BlastItem = memo(({ blast, className }: BlastItemProps) => {
  const { name, polygon, mine_long_name, mine_mas_code, latest_domain_generation_job } = blast;
  const { jobStatus, jobState: optimisationsJobState, triggerBuild } = useBlastOptimisationJob(blast);

  const { data: blastOptimisations, isLoading: isLoadingOptimisations } = useQuery({
    ...optimiserByBlastQueryOptions(name),
    enabled: jobStatus === 'completed',
  });

  const { data: blastDesignContext, isLoading: isLoadingBlastDesignContext } = useQuery({
    ...hardnessDomainByBlastQueryOptions(name),
    enabled: latest_domain_generation_job?.status === 'completed',
  });

  const optimisations = blastOptimisations?.optimisations ?? [];
  const bestOptimisation =
    optimisations.length > 0
      ? optimisations.reduce((best, current) => (current.efficiency > best.efficiency ? current : best))
      : undefined;

  return (
    <Card className={clsx('group transition-shadow duration-300 hover:shadow-lg', className)}>
      {/* Header */}
      <CardHeader className="flex flex-row items-center gap-4">
        <div className="h-16 w-23 text-muted-foreground transition-colors duration-300 group-hover:text-primary group-focus-visible:text-primary lg:h-18">
          <SvgCanvas boundedByGeometries={[polygon]} zoomEnabled={false}>
            <Polygon
              fill="currentColor"
              fillOpacity="0.2"
              stroke="currentColor"
              strokeWidth={0.25}
              geometry={polygon}
            />
          </SvgCanvas>
        </div>
        <div className="flex flex-col gap-0.5">
          <h3 className="font-bold text-base transition-colors duration-300 group-hover:text-primary group-focus-visible:text-primary">
            {name}
          </h3>
          <p className="text-xs">
            {mine_long_name} ({mine_mas_code})
          </p>
        </div>
      </CardHeader>

      {/* Body */}
      <CardContent className="flex grow flex-col space-y-4">
        <div className="w-full space-y-4">
          {isLoadingOptimisations ? (
            <div className="space-y-1">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-full" />
            </div>
          ) : (
            <BlastItemOptimisationSummary
              optimisation={bestOptimisation}
              optimisationsJobState={optimisationsJobState}
            />
          )}
        </div>
        <div className="w-full space-y-4">
          {isLoadingBlastDesignContext ? (
            <div className="space-y-1">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-full" />
            </div>
          ) : (
            <BlastItemDesignContextSummary blastDesignContext={blastDesignContext} />
          )}
        </div>
      </CardContent>

      {/* Footer / call to actions */}
      <CardFooter className="flex-col items-stretch">
        <BlastItemActions
          blastName={name}
          hasOptimisations={optimisations.length > 0}
          isLoadingOptimisations={isLoadingOptimisations}
          isLoadingBlastDesignContext={isLoadingBlastDesignContext}
          optimisationsJobState={optimisationsJobState}
          onBuildOptimisations={triggerBuild}
        />
      </CardFooter>
    </Card>
  );
});
BlastItem.displayName = 'BlastItem';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlastItem };
